import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { valueLoopSteps } from '../../data/mockData';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const colorMap = {
  cyan: { text: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/40', glow: 'box-glow-cyan', hex: '#06b6d4' },
  amber: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/40', glow: 'box-glow-amber', hex: '#ffaa00' },
  fuchsia: { text: 'text-fuchsia-400', bg: 'bg-fuchsia-500/10', border: 'border-fuchsia-500/40', glow: 'box-glow-fuchsia', hex: '#d946ef' },
  emerald: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/40', glow: 'box-glow-cyan', hex: '#22c55e' },
};

export default function ValueLoop() {
  const [activeStep, setActiveStep] = useState(null);

  return (
    <div className="space-y-4">
      {valueLoopSteps.map((step, i) => {
        const c = colorMap[step.color];
        const isActive = activeStep === i;

        return (
          <motion.div
            key={step.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.12, duration: 0.5 }}
            className="relative"
          >
            {/* Connector Line */}
            {i < valueLoopSteps.length - 1 && (
              <div className="absolute left-6 top-full w-px h-4 bg-zinc-800 z-0" />
            )}

            <button
              onMouseEnter={() => setActiveStep(i)}
              onMouseLeave={() => setActiveStep(null)}
              className={`w-full text-left flex items-start gap-4 p-4 rounded-lg border transition-all duration-300 ${
                isActive
                  ? `${c.bg} ${c.border} ${c.glow}`
                  : 'bg-zinc-900/30 border-zinc-800/50 hover:border-zinc-700'
              }`}
            >
              {/* Step Number + Icon */}
              <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center border ${
                isActive ? `${c.border} ${c.bg}` : 'border-zinc-800 bg-zinc-950'
              }`}>
                <div className="relative">
                  <SafeIcon
                    icon={FiIcons[step.icon]}
                    className={`text-lg transition-colors ${isActive ? c.text : 'text-zinc-500'}`}
                  />
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[9px] font-mono tracking-widest uppercase ${isActive ? c.text : 'text-zinc-600'}`}>
                    [{String(step.id).padStart(2, '0')}]
                  </span>
                  <span className={`text-[10px] font-bold tracking-widest uppercase ${isActive ? c.text : 'text-zinc-500'}`}>
                    {step.label}
                  </span>
                </div>
                <h3 className={`text-sm font-bold mb-1 transition-colors ${isActive ? 'text-zinc-100' : 'text-zinc-300'}`}>
                  {step.title}
                </h3>
                <p className="text-xs text-zinc-500 leading-relaxed">
                  {step.desc}
                </p>

                {/* Expanded Detail */}
                <motion.div
                  initial={false}
                  animate={{ height: isActive ? 'auto' : 0, opacity: isActive ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="mt-3 pt-3 border-t border-zinc-800/50">
                    <p className={`text-xs font-mono leading-relaxed ${c.text}`}>
                      → {step.detail}
                    </p>
                  </div>
                </motion.div>
              </div>

              {/* Arrow */}
              <div className={`flex-shrink-0 transition-colors ${isActive ? c.text : 'text-zinc-700'}`}>
                <SafeIcon icon={FiIcons.FiChevronRight} className="text-lg" />
              </div>
            </button>
          </motion.div>
        );
      })}
    </div>
  );
}